// Patient name + match helpers.
//
// The EMR's `Patient` type was originally designed around a multilingual
// `name: { en, ar }` shape, but the live API returns `firstName` /
// `lastName` (plus `email`, `phone` / `phoneNumber`). Most surfaces store
// the API response into the legacy zustand store via `setPatients(list as
// Patient[])`, which silently passes the type-check but breaks any code
// that reads `p.name`. The home-page New Visit search and the global
// command palette both regressed for that reason.
//
// Use these helpers everywhere you need to display a patient name or
// match a free-text query to a list of loaded patients. They handle
// both shapes (and the rare string-name shape from older mocks).

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPatient = Record<string, any>;

const flat = (s: unknown): string =>
  String(s ?? '').toLowerCase().trim();

const stripWhitespace = (s: string): string => s.replace(/\s+/g, '');

/** Best human-readable name for a patient regardless of shape. */
export const resolvePatientName = (
  p: AnyPatient | null | undefined,
  isRTL = false,
): string => {
  if (!p) return '';
  if (p.firstName || p.lastName) {
    return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
  }
  const n = p.name;
  if (n && typeof n === 'object') {
    const en = typeof n.en === 'string' ? n.en : '';
    const ar = typeof n.ar === 'string' ? n.ar : '';
    return isRTL ? ar || en : en || ar;
  }
  if (typeof n === 'string') return n;
  return '';
};

/** True if the patient matches the given free-text query. */
export const patientMatchesQuery = (
  p: AnyPatient | null | undefined,
  query: string,
): boolean => {
  if (!p) return false;
  const q = flat(query);
  if (!q) return false;

  const fullEn = flat(resolvePatientName(p, false));
  const fullAr = flat(resolvePatientName(p, true));
  const first = flat(p.firstName);
  const last = flat(p.lastName);
  const phone = stripWhitespace(flat(p.phone ?? p.phoneNumber));
  const email = flat(p.email);
  const mrn = flat(p.medicalRecordNumber ?? p.id);
  const qStripped = stripWhitespace(q);

  return (
    fullEn.includes(q) ||
    fullAr.includes(q) ||
    first.includes(q) ||
    last.includes(q) ||
    (qStripped.length > 0 && phone.includes(qStripped)) ||
    email.includes(q) ||
    mrn.includes(q)
  );
};

/** Find a single patient by name from a free-text needle (case-insensitive). */
export const findPatientByName = <T extends AnyPatient>(
  patients: readonly T[],
  needle: string,
): T | undefined => {
  const q = flat(needle);
  if (!q) return undefined;
  // Prefer an exact full-name match first; fall back to partial.
  const exact = patients.find(
    (p) => flat(resolvePatientName(p, false)) === q ||
      flat(resolvePatientName(p, true)) === q,
  );
  if (exact) return exact;
  return patients.find((p) => patientMatchesQuery(p, needle));
};
